// src/content/index.ts
import { useTranslation } from 'react-i18next';

// Define interfaces for each content type
interface IntroContent {
  title: string;
  text: string;
}

interface AboutContent {
  title: string;
  text: string;
  section: string;
}

interface PricingContent {
  title: string;
  text: string;
  button: {
    title: string;
    color?: string;
    linkTo?: string;
  }[];
  section: {
    title: string;
    content: string;
  }[];
}

interface SecurityContent {
  title: string;
  text: string;
  section: {
    title: string;
    content: string;
    icon?: string;
  }[];
}

interface ContactContent {
  title: string;
  text: string;
}

interface FooterContent {
  tagline: string;
  launchApp: string;
  navigation: string;
  home: string;
  pricing: string;
  security: string;
  contact: string;
  allRightsReserved: string;
  language: string;
}

// Get current year for copyright
const currentYear = new Date().getFullYear(); // Uses the actual current year dynamically

// Content structure with proper typing and improved translations
export const content: {
  intro: Record<string, IntroContent>;
  about: Record<string, AboutContent>;
  pricing: Record<string, PricingContent>;
  security: Record<string, SecurityContent>;
  contact: Record<string, ContactContent>; // Contact section was defined but not used in original, keep structure
  footer: Record<string, FooterContent>;
} = {
  intro: {
    en: {
      title: "Transcription made worriless",
      text: "Swift. Secure. No subscriptions. Transcriptions on demand."
    },
    es: {
      title: "Transcripción sin preocupaciones",
      text: "Rápido. Seguro. Sin suscripciones. Transcripciones bajo demanda."
    },
    fr: {
      title: "Transcription sans souci",
      text: "Rapide. Sécurisé. Sans abonnements. Transcriptions à la demande."
    },
    de: {
      // title: "Transkription ohne Sorgen", // Original
      title: "Sorgenfreie Transkription", // More natural/catchy
      text: "Schnell. Sicher. Keine Abonnements. Transkriptionen auf Abruf."
    },
    it: {
      title: "Trascrizione senza preoccupazioni",
      text: "Veloce. Sicuro. Nessun abbonamento. Trascrizioni su richiesta."
    },
    da: {
      title: "Transskription uden bekymringer",
      text: "Hurtig. Sikker. Intet abonnementet. Transskriptioner efter behov."
    },
    sv: {
      // title: "Transkription utan oro", // Original
      title: "Bekymmersfri transkription", // More natural/marketing friendly
      text: "Snabbt. Säkert. Inga prenumerationer. Transkriptioner på begäran."
    },
    no: {
      title: "Transkripsjon uten bekymringer",
      text: "Raskt. Sikkert. Intet abonnement. Transkripsjoner på forespørsel."
    },
    nl: {
      title: "Zorgeloze transcriptie", // Already excellent
      text: "Snel. Veilig. Geen abonnementen. Transcripties op aanvraag."
    }
  },
  about: {
    en: {
      title: "Upload. Edit. Summarize",
      text: "Transform your audio and video files into accurate transcriptions with our state-of-the-art AI technology.",
      section: "to:text"
    },
    es: {
      title: "Sube. Edita. Resume",
      text: "Transforma tus archivos de audio y vídeo en transcripciones precisas con nuestra tecnología de IA de vanguardia.",
      section: "to:text"
    },
    fr: {
      title: "Télécharger. Éditer. Résumer",
      text: "Transformez vos fichiers audio et vidéo en transcriptions précises grâce à notre technologie d'IA de pointe.",
      section: "to:text"
    },
    de: {
      title: "Hochladen. Bearbeiten. Zusammenfassen",
      // text: "Transformieren Sie Ihre Audio- und Videodateien in genaue Transkriptionen mit unserer hochmodernen KI-Technologie.", // Original
      text: "Wandeln Sie Ihre Audio- und Videodateien mit unserer hochmodernen KI-Technologie in präzise Transkriptionen um.", // More natural flow and word choice
      section: "to:text"
    },
    it: {
      title: "Carica. Modifica. Riassumi",
      // text: "Trasforma i tuoi file audio e video in trascrizioni accurate con la nostra tecnologia AI all'avanguardia.", // Original
      text: "Trasforma i tuoi file audio e video in trascrizioni precise con la nostra tecnologia AI all'avanguardia.", // "precise" often preferred over "accurate" here
      section: "to:text"
    },
    da: {
      title: "Upload. Rediger. Opsummer",
      // text: "Transformer dine lyd- og videofiler til nøjagtige transskriptioner med vores avancerede AI-teknologi.", // Original
      text: "Omdan dine lyd- og videofiler til nøjagtige transskriptioner med vores avancerede AI-teknologi.", // More idiomatic verb
      section: "to:text"
    },
    sv: {
      title: "Ladda upp. Redigera. Sammanfatta",
      text: "Omvandla dina ljud- och videofiler till noggranna transkriptioner med vår toppmoderna AI-teknik.", // Already good
      section: "to:text"
    },
    no: {
      title: "Last opp. Rediger. Oppsummer",
      // text: "Transformer lyd- og videofilene dine til nøyaktige transkripsjoner med vår toppmoderne AI-teknologi.", // Original
      text: "Gjør om lyd- og videofilene dine til nøyaktige transkripsjoner med vår toppmoderne AI-teknologi.", // More common phrasing
      section: "to:text"
    },
    nl: {
      title: "Uploaden. Bewerken. Samenvatten",
      // text: "Transformeer uw audio- en videobestanden naar nauwkeurige transcripties met onze geavanceerde AI-technologie.", // Original
      text: "Zet uw audio- en videobestanden om in nauwkeurige transcripties met onze geavanceerde AI-technologie.", // More natural verb phrase
      section: "to:text"
    }
  },
  pricing: {
    en: {
      title: "No subscription. Just on demand.",
      text: "Pay only for what you use with our transparent, pay-as-you-go pricing model. No hidden fees, no commitments.",
      button: [
        {
          title: "Learn More",
          color: "#fff",
          linkTo: "/pricing"
        }
      ],
      section: [
        {
          title: "Use one-time payment or buy credits",
          content: "You can transcribe 1 hour with a one-time payment for €1.99. Or you can use credits and transcribe one hour for €0.75. You can buy 20 hours of transcription for €15. Prices are before region-specific VAT."
        }
      ]
    },
    es: {
      title: "Sin suscripciones. Solo bajo demanda.",
      text: "Paga solo por lo que usas con nuestro modelo de precios transparente de pago por uso. Sin tarifas ocultas, sin compromisos.",
      button: [
        {
          title: "Más información",
          color: "#fff",
          linkTo: "/pricing"
        }
      ],
      section: [
        {
          title: "Usa pago único o compra créditos",
          content: "Puedes transcribir 1 hora con un pago único de €1.99. O puedes usar créditos y transcribir una hora por €0.75. Puedes comprar 20 horas de transcripción por €15. Los precios son antes del IVA específico de cada región."
        }
      ]
    },
    fr: {
      title: "Pas d'abonnements. Juste à la demande.",
      text: "Payez uniquement ce que vous utilisez avec notre modèle de tarification transparent et à l'utilisation. Pas de frais cachés, pas d'engagements.",
      button: [
        {
          title: "En savoir plus",
          color: "#fff",
          linkTo: "/pricing"
        }
      ],
      section: [
        {
          title: "Utilisez un paiement unique ou achetez des crédits",
          content: "Vous pouvez transcrire 1 heure avec un paiement unique de 1,99 €. Ou vous pouvez utiliser des crédits et transcrire une heure pour 0,75 €. Vous pouvez acheter 20 heures de transcription pour 15 €. Les prix sont hors TVA spécifique à chaque région."
        }
      ]
    },
    de: {
      // title: "Keine Abonnements. Nur bei Bedarf.", // Original
      title: "Keine Abonnements. Nur auf Abruf.", // Better aligns with "on demand"
      text: "Zahlen Sie nur für das, was Sie mit unserem transparenten Pay-as-you-go-Preismodell nutzen. Keine versteckten Gebühren, keine Verpflichtungen.",
      button: [
        {
          title: "Mehr erfahren",
          color: "#fff",
          linkTo: "/pricing"
        }
      ],
      section: [
        {
          title: "Einmalzahlung oder Guthaben kaufen",
          // content: "Sie können 1 Stunde mit einer Einmalzahlung für 1,99 € transkribieren. Oder Sie können Guthaben verwenden und eine Stunde für 0,75 € transkribieren. Sie können 20 Stunden Transkription für 15 € kaufen. Die Preise verstehen sich ohne regionenspezifische Mehrwertsteuer." // Original
          content: "Sie können 1 Stunde mit einer Einmalzahlung für 1,99 € transkribieren. Oder Sie können Guthaben verwenden und eine Stunde für 0,75 € transkribieren. Sie können 20 Stunden Transkription für 15 € kaufen. Die Preise verstehen sich zzgl. regionenspezifischer MwSt." // More standard VAT phrasing
        }
      ]
    },
    it: {
      title: "Nessun abbonamento. Solo su richiesta.",
      text: "Paga solo per ciò che utilizzi con il nostro modello di prezzo trasparente pay-as-you-go. Nessun costo nascosto, nessun impegno.",
      button: [
        {
          title: "Scopri di più",
          color: "#fff",
          linkTo: "/pricing"
        }
      ],
      section: [
        {
          title: "Usa il pagamento una tantum o acquista crediti",
          // content: "Puoi trascrivere 1 ora con un pagamento una tantum di € 1,99. Oppure puoi utilizzare i crediti e trascrivere un'ora per € 0,75. Puoi acquistare 20 ore di trascrizione per € 15. I prezzi sono al netto dell'IVA specifica per regione." // Original (Incorrect VAT logic)
          content: "Puoi trascrivere 1 ora con un pagamento una tantum di € 1,99. Oppure puoi utilizzare i crediti e trascrivere un'ora per € 0,75. Puoi acquistare 20 ore di trascrizione per € 15. I prezzi si intendono IVA specifica per regione esclusa." // Correct VAT phrasing
        }
      ]
    },
    da: {
      // title: "Intet abonnement. Kun on demand.", // Original (Denglish)
      title: "Intet abonnement. Kun efter behov.", // More natural Danish
      text: "Betal kun for det, du bruger, med vores gennemsigtige pay-as-you-go prismodel. Ingen skjulte gebyrer, ingen forpligtelser.",
      button: [
        {
          // title: "Lær mere", // Original
          title: "Læs mere", // Standard button text
          color: "#fff",
          linkTo: "/pricing"
        }
      ],
      section: [
        {
          title: "Brug engangsbetaling eller køb credits",
          // content: "Du kan transskribere 1 time med en engangsbetaling på €1,99. Eller du kan bruge credits og transskribere en time for €0,75. Du kan købe 20 timers transskription for €15. Priserne er før regionsspecifik moms." // Original
          content: "Du kan transskribere 1 time med en engangsbetaling på €1,99. Eller du kan bruge credits og transskribere en time for €0,75. Du kan købe 20 timers transskription for €15. Priserne er eksklusive regionsspecifik moms." // Standard VAT phrasing
        }
      ]
    },
    sv: {
      // title: "Inga prenumerationer. Bara on demand.", // Original (Swenglish)
      title: "Inga prenumerationer. Endast vid behov.", // More natural Swedish
      text: "Betala endast för det du använder med vår transparenta pay-as-you-go-prismodell. Inga dolda avgifter, inga åtaganden.",
      button: [
        {
          title: "Läs mer", // Already correct
          color: "#fff",
          linkTo: "/pricing"
        }
      ],
      section: [
        {
          title: "Använd engångsbetalning eller köp krediter",
          // content: "Du kan transkribera 1 timme med en engångsbetalning för €1,99. Eller så kan du använda krediter och transkribera en timme för €0,75. Du kan köpa 20 timmars transkription för €15. Priserna är före regionsspecifik moms." // Original
          content: "Du kan transkribera 1 timme med en engångsbetalning för €1,99. Eller så kan du använda krediter och transkribera en timme för €0,75. Du kan köpa 20 timmars transkription för €15. Priserna är exklusive regionsspecifik moms." // Standard VAT phrasing
        }
      ]
    },
    no: {
      title: "Ingen abonnementer. Bare på forespørsel.", // Already okay
      text: "Betal kun for det du bruker med vår transparente pay-as-you-go-prismodell. Ingen skjulte gebyrer, ingen forpliktelser.",
      button: [
        {
          // title: "Lær mer", // Original
          title: "Les mer", // Standard button text
          color: "#fff",
          linkTo: "/pricing"
        }
      ],
      section: [
        {
          title: "Bruk engangsbetaling eller kjøp kreditter",
          // content: "Du kan transkribere 1 time med en engangsbetaling på €1,99. Eller du kan bruke kreditter og transkribere en time for €0,75. Du kan kjøpe 20 timer med transkripsjon for €15. Prisene er før regionsspesifikk mva." // Original
          content: "Du kan transkribere 1 time med en engangsbetaling på €1,99. Eller du kan bruke kreditter og transkribere en time for €0,75. Du kan kjøpe 20 timer med transkripsjon for €15. Prisene er eksklusiv regionspesifikk mva." // Standard VAT phrasing
        }
      ]
    },
    nl: {
      title: "Geen abonnementen. Alleen op aanvraag.", // Already excellent
      text: "Betaal alleen voor wat u gebruikt met ons transparante pay-as-you-go prijsmodel. Geen verborgen kosten, geen verplichtingen.",
      button: [
        {
          title: "Meer informatie", // Already correct
          color: "#fff",
          linkTo: "/pricing"
        }
      ],
      section: [
        {
          title: "Gebruik eenmalige betaling of koop credits",
          content: "U kunt 1 uur transcriberen met een eenmalige betaling van €1,99. Of u kunt credits gebruiken en een uur transcriberen voor €0,75. U kunt 20 uur transcriptie kopen voor €15. De prijzen zijn exclusief regiospecifieke BTW." // Already correct
        }
      ]
    }
  },
  security: {
    // NOTE: List content below uses '\n• ' to denote separate points for better structure.
    // Ensure your frontend renders these newlines and potentially the bullet character correctly,
    // or adapt the rendering logic (e.g., split string by '\n').
    en: {
      title: "\"You own your own files. You decide when they are deleted.\"", // Standard quotes
      text: "We prioritize your data security. All files are encrypted, stored on EU-based, GDPR-compliant servers. - Thomas Hanscomb, Co-founder",
      section: [
        {
          title: "Data Storage and Processing",
          content: "• Your files are stored in Google Cloud's secure data centers in Finland, ensuring full GDPR compliance and European data sovereignty.\n• All transcription processing takes place exclusively in Sweden through our secure processing partners.\n• Data transfer occurs via HTTPS encrypted protocols, protecting your content during upload and download.\n• We maintain strict access controls and security protocols aligned with industry best practices."
        },
        {
          title: "Your Data Rights and Control",
          content: "• You maintain complete ownership of your data at all times.\n• Files can be deleted instantly upon request.\n• We immediately and permanently remove your data when you ask us to.\n• Access is restricted to your authorized account only.\n• Your files are used solely for providing transcription services to you."
        },
        {
          title: "Our Security Commitment",
          content: "• We follow encryption standards similar to major cloud providers (Google, Microsoft, Dropbox).\n• While a small team of trusted employees technically could access files during processing, we:\n  - Maintain strict internal access controls.\n  - Never access content without explicit permission.\n  - Automatically delete files after processing (unless storage is requested).\n  - Follow rigorous data handling protocols." // Indented sub-list for clarity
        },
        {
          title: "Transparency Note",
          content: "We believe in being transparent about our security measures. While we implement comprehensive protections, no online service can guarantee absolute security. We continuously update our security measures to maintain the highest standards of data protection. If you have any questions or concerns about our security measures, please don't hesitate to contact us at anders@totext.eu.",
        }
      ]
    },
    es: {
      title: "«Tú eres dueño de tus archivos. Tú decides cuándo se eliminan.»", // Spanish quotes
      text: "Priorizamos la seguridad de tus datos. Todos los archivos están encriptados, almacenados en servidores con sede en la UE, compatibles con GDPR. - Thomas Hanscomb, Co-fundador",
      section: [
        {
          title: "Almacenamiento y Procesamiento de Datos",
          content: "• Tus archivos se almacenan en los centros de datos seguros de Google Cloud en Finlandia, garantizando el cumplimiento total de GDPR y la soberanía europea de datos.\n• Todo el procesamiento de transcripción tiene lugar exclusivamente en Suecia a través de nuestros socios de procesamiento seguro.\n• La transferencia de datos ocurre a través de protocolos encriptados HTTPS, protegiendo tu contenido durante la carga y descarga.\n• Mantenemos controles de acceso estrictos y protocolos de seguridad alineados con las mejores prácticas de la industria."
        },
        {
          title: "Tus Derechos de Datos y Control",
          content: "• Mantienes la propiedad completa de tus datos en todo momento.\n• Los archivos pueden eliminarse instantáneamente a petición.\n• Eliminamos inmediata y permanentemente tus datos cuando nos lo pides.\n• El acceso está restringido solo a tu cuenta autorizada.\n• Tus archivos se utilizan exclusivamente para proporcionarte servicios de transcripción."
        },
        {
          title: "Nuestro Compromiso de Seguridad",
          content: "• Seguimos estándares de encriptación similares a los principales proveedores de la nube (Google, Microsoft, Dropbox).\n• Aunque un pequeño equipo de empleados de confianza técnicamente podría acceder a los archivos durante el procesamiento, nosotros:\n  - Mantenemos controles internos de acceso estrictos.\n  - Nunca accedemos al contenido sin permiso explícito.\n  - Eliminamos automáticamente los archivos después del procesamiento (a menos que se solicite almacenamiento).\n  - Seguimos protocolos rigurosos de manejo de datos."
        },
        {
          title: "Nota de Transparencia",
          content: "Creemos en ser transparentes sobre nuestras medidas de seguridad. Aunque implementamos protecciones completas, ningún servicio en línea puede garantizar seguridad absoluta. Actualizamos continuamente nuestras medidas de seguridad para mantener los más altos estándares de protección de datos. Si tienes alguna pregunta o inquietud sobre nuestras medidas de seguridad, no dudes en contactarnos en anders@totext.eu."
        }
      ]
    },
    fr: {
      title: "« Vous possédez vos propres fichiers. Vous décidez quand ils sont supprimés. »", // French quotes
      text: "Nous donnons la priorité à la sécurité de vos données. Tous les fichiers sont cryptés, stockés sur des serveurs basés dans l'UE, conformes au RGPD. - Thomas Hanscomb, Co-fondateur",
      section: [
        {
          title: "Stockage et traitement des données",
          content: "• Vos fichiers sont stockés dans les centres de données sécurisés de Google Cloud en Finlande, garantissant une conformité totale au RGPD et la souveraineté européenne des données.\n• Tout le traitement de transcription a lieu exclusively en Suède par l'intermédiaire de nos partenaires de traitement sécurisés.\n• Le transfert de données se fait via des protocoles cryptés HTTPS, protégeant votre contenu pendant le téléchargement.\n• Nous maintenons des contrôles d'accès stricts et des protocoles de sécurité alignés sur les meilleures pratiques de l'industrie."
        },
        {
          title: "Vos droits sur les données et votre contrôle",
          content: "• Vous conservez la propriété complète de vos données à tout moment.\n• Les fichiers peuvent être supprimés instantanément sur demande.\n• Nous supprimons immédiatement et définitivement vos données lorsque vous nous le demandez.\n• L'accès est limité uniquement à votre compte autorisé.\n• Vos fichiers sont utilisés uniquement pour vous fournir des services de transcription."
        },
        {
          title: "Notre engagement en matière de sécurité",
          content: "• Nous suivons des normes de cryptage similaires à celles des principaux fournisseurs de cloud (Google, Microsoft, Dropbox).\n• Bien qu'une petite équipe d'employés de confiance puisse techniquement accéder aux fichiers pendant le traitement, nous :\n  - Maintenons des contrôles d'accès internes stricts.\n  - N'accédons jamais au contenu sans autorisation explicite.\n  - Supprimons automatiquement les fichiers après le traitement (sauf si le stockage est demandé).\n  - Suivons des protocoles rigoureux de gestion des données."
        },
        {
          title: "Note de transparence",
          content: "Nous croyons en la transparence concernant nos mesures de sécurité. Bien que nous mettions en œuvre des protections complètes, aucun service en ligne ne peut garantir une sécurité absolue. Nous mettons continuellement à jour nos mesures de sécurité pour maintenir les normes les plus élevées de protection des données. Si vous avez des questions ou des préoccupations concernant nos mesures de sécurité, n'hésitez pas à nous contacter à anders@totext.eu."
        }
      ]
    },
    de: {
      title: "„Sie besitzen Ihre eigenen Dateien. Sie entscheiden, wann sie gelöscht werden.“", // German quotes
      text: "Wir priorisieren die Sicherheit Ihrer Daten. Alle Dateien werden verschlüsselt und auf EU-basierten, DSGVO-konformen Servern gespeichert. - Thomas Hanscomb, Mitbegründer",
      section: [
        {
          title: "Datenspeicherung und -verarbeitung",
          content: "• Ihre Dateien werden in den sicheren Rechenzentren von Google Cloud in Finnland gespeichert, was die vollständige DSGVO-Konformität und europäische Datensouveränität gewährleistet.\n• Die gesamte Transkriptionsverarbeitung findet ausschließlich in Schweden über unsere sicheren Verarbeitungspartner statt.\n• Die Datenübertragung erfolgt über HTTPS-verschlüsselte Protokolle, die Ihre Inhalte während des Hoch- und Herunterladens schützen.\n• Wir halten uns an strenge Zugangskontrollen und Sicherheitsprotokolle, die an den Branchenstandards ausgerichtet sind."
        },
        {
          title: "Ihre Datenrechte und Kontrolle",
          content: "• Sie behalten jederzeit die vollständigen Eigentumsrechte an Ihren Daten.\n• Dateien können auf Anfrage sofort gelöscht werden.\n• Wir entfernen Ihre Daten sofort und dauerhaft, wenn Sie uns darum bitten.\n• Der Zugriff ist nur auf Ihr autorisiertes Konto beschränkt.\n• Ihre Dateien werden ausschließlich zur Bereitstellung von Transkriptionsdiensten für Sie verwendet."
        },
        {
          title: "Unser Sicherheitsversprechen",
          content: "• Wir folgen Verschlüsselungsstandards ähnlich wie bei großen Cloud-Anbietern (Google, Microsoft, Dropbox).\n• Obwohl ein kleines Team vertrauenswürdiger Mitarbeiter technisch während der Verarbeitung auf Dateien zugreifen könnte, :\n  - halten wir strenge interne Zugangskontrollen ein.\n  - greifen wir nie ohne ausdrückliche Genehmigung auf Inhalte zu.\n  - löschen wir Dateien nach der Verarbeitung automatisch (es sei denn, Speicherung wird angefordert).\n  - folgen wir rigorosen Datenverarbeitungsprotokollen."
        },
        {
          title: "Transparenzhinweis",
          content: "Wir glauben an Transparenz bezüglich unserer Sicherheitsmaßnahmen. Obwohl wir umfassende Schutzmaßnahmen implementieren, kann kein Online-Dienst absolute Sicherheit garantieren. Wir aktualisieren kontinuierlich unsere Sicherheitsmaßnahmen, um die höchsten Standards des Datenschutzes zu gewährleisten. Wenn Sie Fragen oder Bedenken zu unseren Sicherheitsmaßnahmen haben, zögern Sie nicht, uns unter anders@totext.eu zu kontaktieren."
        }
      ]
    },
    it: {
      title: "«Sei tu il proprietario dei tuoi file. Sei tu a decidere quando vengono eliminati.»", // Italian quotes
      text: "Diamo priorità alla sicurezza dei tuoi dati. Tutti i file sono crittografati, archiviati su server basati nell'UE, conformi al GDPR. - Thomas Hanscomb, Co-fondatore",
      section: [
        {
          title: "Archiviazione e elaborazione dei dati",
          content: "• I tuoi file sono archiviati nei centri dati sicuri di Google Cloud in Finlandia, garantendo la piena conformità al GDPR e la sovranità europea dei dati.\n• Tutta l'elaborazione della trascrizione avviene esclusivamente in Svezia attraverso i nostri partner di elaborazione sicuri.\n• Il trasferimento dei dati avviene tramite protocolli crittografati HTTPS, proteggendo i tuoi contenuti durante il caricamento e il download.\n• Manteniamo controlli di accesso rigorosi e protocolli di sicurezza allineati con le migliori pratiche del settore."
        },
        {
          title: "I tuoi diritti sui dati e il controllo",
          content: "• Mantieni la proprietà completa dei tuoi dati in ogni momento.\n• I file possono essere eliminati istantaneamente su richiesta.\n• Rimuoviamo immediatamente e permanentemente i tuoi dati quando ce lo chiedi.\n• L'accesso è limitato solo al tuo account autorizzato.\n• I tuoi file vengono utilizzati esclusivamente per fornirti servizi di trascrizione."
        },
        {
          title: "Il nostro impegno per la sicurezza",
          content: "• Seguiamo standard di crittografia simili a quelli dei principali fornitori cloud (Google, Microsoft, Dropbox).\n• Anche se un piccolo team di dipendenti fidati potrebbe tecnicamente accedere ai file durante l'elaborazione, noi:\n  - Manteniamo controlli di accesso interni rigorosi.\n  - Non accediamo mai ai contenuti senza autorizzazione esplicita.\n  - Eliminiamo automaticamente i file dopo l'elaborazione (a meno che non venga richiesto lo stoccaggio).\n  - Seguiamo rigorosi protocolli di gestione dei dati."
        },
        {
          title: "Nota sulla trasparenza",
          content: "Crediamo nella trasparenza riguardo alle nostre misure di sicurezza. Anche se implementiamo protezioni complete, nessun servizio online può garantire una sicurezza assoluta. Aggiorniamo continuamente le nostre misure di sicurezza per mantenere i più alti standard di protezione dei dati. Se hai domande o preoccupazioni sulle nostre misure di sicurezza, non esitare a contattarci a anders@totext.eu."
        }
      ]
    },
    da: {
      title: "„Du ejer dine egne filer. Du bestemmer, hvornår de slettes.“", // Danish quotes
      text: "Vi prioriterer sikkerheden af dine data. Alle filer er krypterede, opbevaret på EU-baserede, GDPR-kompatible servere. - Thomas Hanscomb, Medstifter",
      section: [
        {
          title: "Dataopbevaring og -behandling",
          content: "• Dine filer opbevares i Google Clouds sikre datacentre i Finland, hvilket sikrer fuld GDPR-overholdelse og europæisk datasuverænitet.\n• Al transskriptionsbehandling finder udelukkende sted i Sverige gennem vores sikre behandlingspartnere.\n• Dataoverførsel sker via HTTPS-krypterede protokoller, der beskytter dit indhold under upload og download.\n• Vi opretholder strenge adgangskontroller og sikkerhedsprotokoller, der er på linje med branchens bedste praksis."
        },
        {
          title: "Dine datarettigheder og kontrol",
          content: "• Du bevarer til enhver tid det fulde ejerskab over dine data.\n• Filer kan slettes øjeblikkeligt efter anmodning.\n• Vi fjerner straks og permanent dine data, når du beder os om det.\n• Adgang er begrænset til kun din autoriserede konto.\n• Dine filer bruges udelukkende til at levere transskriptionstjenester til dig."
        },
        {
          title: "Vores sikkerhedsforpligtelse",
          content: "• Vi følger krypteringsstandarder svarende til større cloudleverandører (Google, Microsoft, Dropbox).\n• Selvom et lille team af betroede medarbejdere teknisk set kunne få adgang til filer under behandling, så:\n  - opretholder vi strenge interne adgangskontroller.\n  - får vi aldrig adgang til indhold uden udtrykkelig tilladelse.\n  - sletter vi automatisk filer efter behandling (medmindre opbevaring er anmodet).\n  - følger vi strenge databehandlingsprotokoller."
        },
        {
          // title: "Transparentnote", // Original
          title: "Note om gennemsigtighed", // More natural phrasing
          content: "Vi tror på at være transparente om vores sikkerhedsforanstaltninger. Selvom vi implementerer omfattende beskyttelse, kan ingen online-tjeneste garantere absolut sikkerhed. Vi opdaterer løbende vores sikkerhedsforanstaltninger for at opretholde de højeste standarder for databeskyttelse. Hvis du har spørgsmål eller bekymringer om vores sikkerhedsforanstaltninger, er du velkommen til at kontakte os på anders@totext.eu."
        }
      ]
    },
    sv: {
      title: "”Du äger dina egna filer. Du bestämmer när de raderas.”", // Swedish quotes
      text: "Vi prioriterar säkerheten för dina data. Alla filer är krypterade och lagras på EU-baserade, GDPR-kompatibla servrar. - Thomas Hanscomb, Medgrundare", // Lowercase 'm' typical for titles
      section: [
        {
          title: "Datalagring och bearbetning",
          content: "• Dina filer lagras i Google Clouds säkra datacenter i Finland, vilket säkerställer full GDPR-efterlevnad och europeisk datasuveränitet.\n• All transkriptionsbearbetning sker uteslutande i Sverige genom våra säkra bearbetningspartners.\n• Dataöverföring sker via HTTPS-krypterade protokoll, vilket skyddar ditt innehåll under upp- och nedladdning.\n• Vi upprätthåller strikta åtkomstkontroller och säkerhetsprotokoll i linje med branschens bästa praxis."
        },
        {
          title: "Dina datarättigheter och kontroll",
          content: "• Du behåller full äganderätt till dina data hela tiden.\n• Filer kan raderas omedelbart på begäran.\n• Vi tar bort dina data omedelbart och permanent när du ber oss om det.\n• Åtkomst är begränsad endast till ditt auktoriserade konto.\n• Dina filer används endast för att tillhandahålla transkriptionstjänster till dig."
        },
        {
          title: "Vårt säkerhetsåtagande",
          content: "• Vi följer krypteringsstandarder som liknar stora molnleverantörer (Google, Microsoft, Dropbox).\n• Även om ett litet team av betrodda anställda tekniskt sett skulle kunna få åtkomst till filer under bearbetning, så:\n  - upprätthåller vi strikta interna åtkomstkontroller.\n  - kommer vi aldrig åt innehåll utan uttryckligt tillstånd.\n  - raderar vi automatiskt filer efter bearbetning (om inte lagring begärs).\n  - följer vi rigorösa datahanteringsprotokoll."
        },
        {
          title: "Transparensnotis",
          content: "Vi tror på att vara transparenta om våra säkerhetsåtgärder. Även om vi implementerar omfattande skydd kan ingen onlinetjänst garantera absolut säkerhet. Vi uppdaterar kontinuerligt våra säkerhetsåtgärder för att upprätthålla de högsta standarderna för dataskydd. Om du har frågor eller funderingar om våra säkerhetsåtgärder, tveka inte att kontakta oss på anders@totext.eu."
        }
      ]
    },
    no: {
      title: "«Du eier dine egne filer. Du bestemmer når de slettes.»", // Norwegian quotes
      text: "Vi prioriterer sikkerheten til dataene dine. Alle filer er kryptert, lagret på EU-baserte, GDPR-kompatible servere. - Thomas Hanscomb, Medgründer",
      section: [
        {
          title: "Datalagring og behandling",
          content: "• Filene dine lagres i Google Clouds sikre datasentre i Finland, som sikrer full GDPR-samsvar og europeisk datasuverenitet.\n• All transkripsjonsbehandling skjer utelukkende i Sverige gjennom våre sikre behandlingspartnere.\n• Dataoverføring skjer via HTTPS-krypterte protokoller, som beskytter innholdet ditt under opplasting og nedlasting.\n• Vi opprettholder strenge tilgangskontroller og sikkerhetsprotokoller i tråd med beste praksis i bransjen."
        },
        {
          title: "Dine datarettigheter og kontroll",
          content: "• Du beholder fullt eierskap til dataene dine til enhver tid.\n• Filer kan slettes umiddelbart på forespørsel.\n• Vi fjerner dataene dine umiddelbart og permanent når du ber oss om det.\n• Tilgang er begrenset kun til din autoriserte konto.\n• Filene dine brukes utelukkende til å gi deg transkripsjonstjenester."
        },
        {
          title: "Vårt sikkerhetsløfte",
          content: "• Vi følger krypteringsstandarder som ligner store skyleverandører (Google, Microsoft, Dropbox).\n• Selv om et lite team av betrodde ansatte teknisk sett kunne få tilgang til filer under behandling, så:\n  - opprettholder vi strenge interne tilgangskontroller.\n  - får vi aldri tilgang til innhold uten uttrykkelig tillatelse.\n  - sletter vi automatisk filer etter behandling (med mindre lagring er forespurt).\n  - følger vi strenge databehandlingsprotokoller."
        },
        {
          title: "Transparensnotat",
          content: "Vi tror på å være transparente om våre sikkerhetstiltak. Selv om vi implementerer omfattende beskyttelse, kan ingen nettbasert tjeneste garantere absolutt sikkerhet. Vi oppdaterer kontinuerlig våre sikkerhetstiltak for å opprettholde de høyeste standardene for databeskyttelse. Hvis du har spørsmål eller bekymringer angående våre sikkerhetstiltak, ikke nøl med å kontakte oss på anders@totext.eu."
        }
      ]
    },
    nl: {
      title: "„U bezit uw eigen bestanden. U beslist wanneer ze worden verwijderd.“", // Dutch quotes
      text: "We geven prioriteit aan de beveiliging van uw gegevens. Alle bestanden zijn versleuteld, opgeslagen op in de EU gebaseerde, GDPR-conforme servers. - Thomas Hanscomb, Mede-oprichter",
      section: [
        {
          title: "Gegevensopslag en -verwerking",
          content: "• Uw bestanden worden opgeslagen in de beveiligde datacenters van Google Cloud in Finland, wat volledige GDPR-naleving en Europese gegevenssouvereiniteit garandeert.\n• Alle transcriptieverwerking vindt uitsluitend plaats in Zweden via onze beveiligde verwerkingspartners.\n• Gegevensoverdracht gebeurt via HTTPS-versleutelde protocollen, die uw inhoud beschermen tijdens het uploaden en downloaden.\n• We handhaven strikte toegangscontroles en beveiligingsprotocollen in lijn met de beste praktijken in de industrie."
        },
        {
          title: "Uw gegevensrechten en controle",
          content: "• U behoudt te allen tijde volledig eigendom van uw gegevens.\n• Bestanden kunnen onmiddellijk worden verwijderd op verzoek.\n• We verwijderen uw gegevens onmiddellijk en permanent wanneer u ons daarom vraagt.\n• Toegang is beperkt tot alleen uw geautoriseerde account.\n• Uw bestanden worden uitsluitend gebruikt om u transcriptiediensten te leveren."
        },
        {
          title: "Onze toewijding aan beveiliging",
          content: "• We volgen versleutelingsstandaarden vergelijkbaar met grote cloudproviders (Google, Microsoft, Dropbox).\n• Hoewel een klein team van vertrouwde medewerkers technisch toegang zou kunnen krijgen tot bestanden tijdens de verwerking, :\n  - handhaven we strikte interne toegangscontroles.\n  - hebben we nooit zonder expliciete toestemming toegang tot inhoud.\n  - verwijderen we automatisch bestanden na verwerking (tenzij opslag wordt gevraagd).\n  - volgen we strikte gegevensverwerkingsprotocollen."
        },
        {
          title: "Transparantievermelding",
          content: "We geloven in transparantie over onze beveiligingsmaatregelen. Hoewel we uitgebreide bescherming implementeren, kan geen enkele online dienst absolute veiligheid garanderen. We werken voortdurend onze beveiligingsmaatregelen bij om de hoogste normen voor gegevensbescherming te handhaven. Als u vragen of zorgen heeft over onze beveiligingsmaatregelen, neem dan gerust contact met ons op via anders@totext.eu." // Added missing contact email here
        }
      ]
    }
  },
  contact: {
     // Define content for the contact section if needed, following the interface
     // Example:
     en: {
       title: "Get in Touch",
       text: "Have questions? We'd love to hear from you."
     },
     es: {
      title: "Ponerse en contacto",
      text: "¿Tienes preguntas? Nos encantaría saber de ti."
     },
     fr: {
       title: "Contactez-nous",
       text: "Avez-vous des questions ? Nous aimerions avoir de vos nouvelles."
     },
     de: {
       title: "Kontakt aufnehmen",
       text: "Haben Sie Fragen? Wir würden uns freuen, von Ihnen zu hören."
     },
     it: {
      title: "Contattaci",
      text: "Hai domande? Ci piacerebbe sentirti."
     },
     da: {
       title: "Kontakt os",
       text: "Har du spørgsmål? Vi vil meget gerne høre fra dig."
     },
     sv: {
       title: "Kontakta oss",
       text: "Har du frågor? Vi hör gärna från dig."
     },
     no: {
       title: "Ta kontakt",
       text: "Har du spørsmål? Vi vil gjerne høre fra deg."
     },
     nl: {
       title: "Neem contact op",
       text: "Heeft u vragen? We horen graag van u."
     }
  },
  footer: {
    // Added footer translations based on standard terms
    // Assuming Company Name is 'ToText' and using current year
    en: {
      tagline: "Transcription made simple.", // Example tagline
      launchApp: "Launch App",
      navigation: "Navigation",
      home: "Home",
      pricing: "Pricing",
      security: "Security",
      contact: "Contact",
      allRightsReserved: `© ${currentYear} ToText. All rights reserved.`,
      language: "Language"
    },
    es: {
      tagline: "Transcripción simplificada.",
      launchApp: "Abrir App",
      navigation: "Navegación",
      home: "Inicio",
      pricing: "Precios",
      security: "Seguridad",
      contact: "Contacto",
      allRightsReserved: `© ${currentYear} ToText. Todos los derechos reservados.`,
      language: "Idioma"
    },
    fr: {
      tagline: "La transcription simplifiée.",
      launchApp: "Lancer l'App",
      navigation: "Navigation",
      home: "Accueil",
      pricing: "Tarifs",
      security: "Sécurité",
      contact: "Contact",
      allRightsReserved: `© ${currentYear} ToText. Tous droits réservés.`,
      language: "Langue"
    },
    de: {
      tagline: "Transkription leicht gemacht.",
      launchApp: "App starten",
      navigation: "Navigation",
      home: "Startseite",
      pricing: "Preise",
      security: "Sicherheit",
      contact: "Kontakt",
      allRightsReserved: `© ${currentYear} ToText. Alle Rechte vorbehalten.`,
      language: "Sprache"
    },
    it: {
      tagline: "Trascrizione resa semplice.",
      launchApp: "Avvia App",
      navigation: "Navigazione",
      home: "Home",
      pricing: "Prezzi",
      security: "Sicurezza",
      contact: "Contatti",
      allRightsReserved: `© ${currentYear} ToText. Tutti i diritti riservati.`,
      language: "Lingua"
    },
    da: {
      tagline: "Transskription gjort enkelt.",
      launchApp: "Start App",
      navigation: "Navigation",
      home: "Forside",
      pricing: "Priser",
      security: "Sikkerhed",
      contact: "Kontakt",
      allRightsReserved: `© ${currentYear} ToText. Alle rettigheder forbeholdes.`,
      language: "Sprog"
    },
    sv: {
      tagline: "Transkribering på ett enkelt sätt.",
      launchApp: "Starta App",
      navigation: "Navigering",
      home: "Hem",
      pricing: "Priser",
      security: "Säkerhet",
      contact: "Kontakt",
      allRightsReserved: `© ${currentYear} ToText. Alla rättigheter förbehållna.`,
      language: "Språk"
    },
    no: {
      tagline: "Transkripsjon gjort enkelt.",
      launchApp: "Start App",
      navigation: "Navigasjon",
      home: "Hjem",
      pricing: "Priser",
      security: "Sikkerhet",
      contact: "Kontakt",
      allRightsReserved: `© ${currentYear} ToText. Alle rettigheter forbeholdt.`,
      language: "Språk"
    },
    nl: {
      tagline: "Transcriptie eenvoudig gemaakt.",
      launchApp: "Start App",
      navigation: "Navigatie",
      home: "Home",
      pricing: "Prijzen",
      security: "Beveiliging",
      contact: "Contact",
      allRightsReserved: `© ${currentYear} ToText. Alle rechten voorbehouden.`,
      language: "Taal"
    }
  }
};

export function useContent(contentKey: 'intro'): IntroContent;
export function useContent(contentKey: 'about'): AboutContent;
export function useContent(contentKey: 'pricing'): PricingContent;
export function useContent(contentKey: 'security'): SecurityContent;
export function useContent(contentKey: 'contact'): ContactContent;
export function useContent(contentKey: 'footer'): FooterContent;
export function useContent(contentKey: keyof typeof content) {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const fallbackLanguage = 'en';
  
  // Return content in current language or fallback to English
  return (
    (currentLanguage in content[contentKey] 
      ? content[contentKey][currentLanguage] 
      : content[contentKey][fallbackLanguage])
  );
}